import {
  Outlet,
  Link as RouterLink,
  useMatch,
  useParams,
} from "react-router-dom";
import { useTeam } from "../TeamLayout";
import {
  Box,
  Breadcrumbs,
  Button,
  Link,
  TabList,
  Tabs,
  Typography,
} from "@mui/joy";
import Tab, { tabClasses } from "@mui/joy/Tab";
import { useTranslation } from "../../../i18n";
import HomeIcon from "@mui/icons-material/Home";
import { useInspectionDetailsByRefQuery } from "../../../__generated__/types-and-hooks";
import DownloadIcon from "@mui/icons-material/Download";
import { Stack } from "@mui/material";
import { InspectionPDF } from "./inspection/ExportToPdf";
import { pdf } from "@react-pdf/renderer";
import OnlineUsersToggle from "../../../components/OnlineUsersToggle";

async function downloadPDF(inspection: any) {
  const blob = await pdf(<InspectionPDF inspection={inspection} />).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `inspection-${inspection.uniqueId}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Inspection() {
  const teamCtx = useTeam();

  // Match each possible path
  const samplingMatch = useMatch("/:teamSlug/inspections/:ref/sampling");
  const supplierMatch = useMatch("/:teamSlug/inspections/:ref/supplier");
  const filesMatch = useMatch("/:teamSlug/inspections/:ref/files");
  const activityMatch = useMatch("/:teamSlug/inspections/:ref/activity");
  const agencyMatch = useMatch("/:teamSlug/inspections/:ref/agency");
  const chatMatch = useMatch("/:teamSlug/inspections/:ref/chat");

  // Determine which subpath is active
  let activeTab = "details"; // Default
  if (samplingMatch) activeTab = "sampling";
  if (supplierMatch) activeTab = "supplier";
  if (filesMatch) activeTab = "files";
  if (activityMatch) activeTab = "activity";
  if (agencyMatch) activeTab = "agency";
  if (chatMatch) activeTab = "chat";

  const { ref } = useParams();
  const t = useTranslation();
  const inspection = useInspectionDetailsByRefQuery({
    variables: { uniqueId: ref! },
  });

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        m: 0,
        p: 0,
      }}
    >
      <Stack direction="row">
        <Breadcrumbs
          aria-label="breadcrumbs"
          sx={{
            "--Breadcrumbs-gap": "4px",
            m: 1,
          }}
          size="sm"
        >
          <Link component={RouterLink} color="neutral" to="/">
            <HomeIcon />
          </Link>
          <Link
            component={RouterLink}
            color="neutral"
            to={`/${teamCtx.team.slug}`}
          >
            {teamCtx.team.name}
          </Link>
          <Link
            component={RouterLink}
            color="neutral"
            to={`/${teamCtx.team.slug}/inspections`}
          >
            {t("Inspections")}
          </Link>
          <Typography level="title-sm">#{ref?.toUpperCase()}</Typography>
        </Breadcrumbs>
        {inspection.data?.inspection_by_unique_id && (
          <Box sx={{ display: "flex", alignSelf: "center" }}>
            <Button
              variant="outlined"
              size="sm"
              onClick={() => {
                downloadPDF(inspection.data?.inspection_by_unique_id);
              }}
              startDecorator={<DownloadIcon />}
            >
              PDF
            </Button>
          </Box>
        )}
        <OnlineUsersToggle />
      </Stack>

      <Tabs
        sx={{
          bgcolor: "transparent",
          m: 0,
          p: 0,
        }}
        value={activeTab}
      >
        <TabList
          variant="outlined"
          disableUnderline
          sx={{
            m: 2,
            mt: 0,
            mb: 0,
            gap: 0.5,
            borderRadius: "sm",
            bgcolor: "background.level2",
            overflow: "hidden",
            [`& .${tabClasses.root}`]: {
              border: 0,
              borderRight: "1px solid",
              borderColor: "divider",
            },
            [`& .${tabClasses.root}:last-of-type`]: {
              borderRight: "none",
            },
            [`& .${tabClasses.root}[aria-selected="true"]`]: {
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)",
              borderRadius: "sm",
              bgcolor: "neutral.900", // Darker for better contrast
              color: "neutral.100",
              fontWeight: "bold",
            },
            [`& .${tabClasses.root}:hover`]: {
              borderRadius: "sm",
              bgcolor: "neutral.800", // Lighter than selected, darker than default
              color: "neutral.50", // Slightly brighter text
              boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.4)", // Stronger shadow for a lift effect
              transition:
                "background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
            },
          }}
        >
          <Tab
            disableIndicator
            component={RouterLink}
            relative="path"
            to={""}
            value="details"
          >
            {t("General")}
          </Tab>
          <Tab
            disableIndicator
            component={RouterLink}
            relative="path"
            to={"sampling"}
            value="sampling"
          >
            {t("SKUs")}
          </Tab>
          {/* <Tab
            disableIndicator
            component={RouterLink}
            relative="path"
            to={"supplier"}
            value="supplier"
          >
            {t("Supplier")}
          </Tab>
          <Tab
            disableIndicator
            component={RouterLink}
            relative="path"
            to={"agency"}
            value="agency"
          >
            {t("Agency")}
          </Tab> */}
          {/* <Tab
            disableIndicator
            component={RouterLink}
            relative="path"
            to={"files"}
            value="files"
          >
            {t("Results")}
          </Tab> */}
          {/* <Tab
            disableIndicator
            component={RouterLink}
            relative="path"
            to={"activity"}
            value="activity"
          >
            {t("Activity")}
          </Tab> */}
          <Tab
            disableIndicator
            component={RouterLink}
            relative="path"
            to={"chat"}
            value="chat"
          >
            {t("Chat")}
          </Tab>
        </TabList>
      </Tabs>

      <Outlet
        context={{
          ...teamCtx,
          inspection: inspection.data?.inspection_by_unique_id,
        }}
      />
    </Box>
  );
}
